/**
 * Página de desarrollo para probar la maquetación del informe de valoración
 * sin conexión. Usa datos mock y no realiza llamadas a la API.
 *
 * Ruta: /asset/sheet-dev
 * Uso: abrir en el navegador para ver portada, perfil, valoración (con yield) y pie.
 */

import { useState, useCallback } from 'react';
import { AssetReportHeader } from '../../components/ValuationReport/ReportHeader/header.module';
import { AssetProfile } from '../../components/ValuationReport/ReportProfile/assetProfile.module';
import { Valuation } from '../../components/ValuationReport/ReportValuation/valuation.module';
import { GlobalStyle, PageModulesContainer } from './assetReport.styles';
import { AssetReportFooter } from '../../components/ValuationReport/ReportFooter/assetReportFooter.component';
import { ReportCover } from '../../components/ValuationReport/ReportCover/cover.component';
import { AgentInfo } from '../../components/ValuationReport/ReportAgentInfo/agentInfo.component';
import { ModuleToggle } from '../../components/ValuationReport/ModuleToggle/moduleToggle.component';
import { defaultModuleVisibility } from '../../components/ValuationReport/ModuleToggle/moduleConfig';
import I18nProvider from '../../i18n/i18n';
import { PreviewPage } from '../../styles/global.styles';
import { AssetProfileDataModel } from '../../types/assetProfile.interface';
import { ValuationDataModel } from '../../types/valuation.interface';
import {
  mockAssetDetails,
  mockReportData,
  mockValuationData,
  mockAssetProfileData,
} from '../../mock/assetReport.mock';

/** Visibilidad por defecto en dev: solo portada, agente, perfil y valoración */
const devModuleVisibility: Record<string, boolean> = {
  ...defaultModuleVisibility,
  cover: true,
  agentInfo: true,
  assetProfile: true,
  valuation: true,
  marketValueGraph: false,
  comparativeGraph: false,
  financialData: false,
  offerDemand: false,
  zonePopulation: false,
  zone: false,
  comparables: false,
  glossary: false,
  terms: false,
  backCover: false,
};

export function AssetReportDev() {
  const [visibleModules, setVisibleModules] =
    useState<Record<string, boolean>>(devModuleVisibility);
  const [reportData] = useState(mockReportData);
  const [initialAssetDetails] = useState(mockAssetDetails);
  const [valuationData] = useState<ValuationDataModel>(mockValuationData);
  const [assetProfileData] = useState<AssetProfileDataModel>(mockAssetProfileData);
  const country = 'es';

  const handleModuleToggle = useCallback((moduleId: string, visible: boolean) => {
    setVisibleModules((prev) => ({ ...prev, [moduleId]: visible }));
  }, []);

  const handleExcludeChange = useCallback(() => {
    // No-op in dev; could extend to update mock reportData if needed
  }, []);

  return (
    <I18nProvider locale="es" country={country}>
      <div className="app-gp">
        <ModuleToggle
          onModuleToggle={handleModuleToggle}
          visibleModules={visibleModules}
          isReportRendered={true}
          selectedLanguage="es"
          onLanguageChange={() => {}}
          onPriceTypeChange={() => {}}
          excludeRent={reportData.exclude_rent}
          excludeSale={reportData.exclude_sale}
          onExcludeChange={handleExcludeChange}
          operation={initialAssetDetails.operation}
          reportType={initialAssetDetails.reportType}
        />
        <GlobalStyle />

        {visibleModules.cover && (
          <PreviewPage id="module-cover" data-module-id="cover">
            <PageModulesContainer>
              <AssetReportHeader
                data={reportData}
                listingType={initialAssetDetails.listingType}
                reportType={initialAssetDetails.reportType}
                operation={initialAssetDetails.operation}
              />
              <ReportCover
                reportData={reportData}
                listingType={initialAssetDetails.listingType}
                operation={initialAssetDetails.operation}
                address={initialAssetDetails.address ?? ''}
              />
              <AssetReportFooter {...reportData} />
            </PageModulesContainer>
          </PreviewPage>
        )}

        {visibleModules.agentInfo && (
          <PreviewPage id="module-agentInfo" data-module-id="agentInfo">
            <PageModulesContainer>
              <AssetReportHeader
                data={reportData}
                listingType={initialAssetDetails.listingType}
                reportType={initialAssetDetails.reportType}
                operation={initialAssetDetails.operation}
              />
              <AgentInfo {...reportData} />
              <AssetReportFooter {...reportData} />
            </PageModulesContainer>
          </PreviewPage>
        )}

        {(visibleModules.assetProfile || visibleModules.valuation) && (
          <PreviewPage id="module-assetProfile" data-module-id="assetProfile">
            <PageModulesContainer>
              <AssetReportHeader
                data={reportData}
                listingType={initialAssetDetails.listingType}
                reportType={initialAssetDetails.reportType}
                operation={initialAssetDetails.operation}
              />
              {visibleModules.assetProfile && (
                <div id="module-assetProfile" data-module-id="assetProfile">
                  <AssetProfile
                    {...assetProfileData}
                    reportType={initialAssetDetails.reportType}
                  />
                </div>
              )}
              {visibleModules.valuation && (
                <div id="module-valuation" data-module-id="valuation">
                  <Valuation {...valuationData} country={country} />
                </div>
              )}
              <AssetReportFooter {...reportData} />
            </PageModulesContainer>
          </PreviewPage>
        )}
      </div>
    </I18nProvider>
  );
}
